/**
 * NewProjectModal — "create new project" form, styled to match the
 * app's iio.css chrome (not Mantine defaults).
 *
 * Calls the same `createProject` Cloud Function the mobile app uses
 * (see `functions/src/createProject.ts`). The callable derives `uid`
 * from `request.auth.uid` and enforces the per-tier project-count
 * cap server-side, so the only thing the client has to do is
 * forward the form fields.
 *
 * MIRRORS the mobile create form:
 *   - Project name   (required, 2–80 chars)
 *   - Site address   (required, 3+ chars)
 *   - Start date     (required)
 *   - End date       (optional, ≥ start date)
 *   - Project value  (required, whole rupees)
 *
 * History: the previous version of this file used Mantine's
 * `<Modal>`, `<TextInput>`, `<PrimaryButton>` wrappers. The Primary
 * button rendered invisible because it referenced `var(--primary)`
 * — which is NOT defined in iio.css (the app uses `--brand` for
 * red CTAs). On top of that the Mantine modal chrome (centered
 * card, plain inputs, light alert) looked nothing like the rest
 * of the app. Now rebuilt as a vanilla centered modal using
 * iio.css tokens + .btn-primary / .btn-secondary classes so it
 * fits in visually with every other slide-over and form in the
 * codebase.
 *
 * Errors surface as an inline red banner inside the modal — no
 * more easy-to-miss top-right toasts.
 */
import { useEffect, useState } from 'react';

import { functions, httpsCallable } from '@/lib/firebase';

export type NewProjectModalProps = {
  opened: boolean;
  onClose: () => void;
  /** Org id to create under — required, comes from `useProjects().orgId`. */
  orgId: string | null;
  /** Fires after a successful create with the new project's id.
   *  Caller typically navigates to `/projects/${projectId}`. */
  onCreated?: (projectId: string) => void;
  /** Optional pre-fills used when spawning the project from
   *  another flow. All optional. */
  defaultName?: string;
  defaultSiteAddress?: string;
  defaultValue?: number;
};

/** Mirrors the server's accepted callable payload — see
 *  `functions/src/createProject.ts` for the canonical shape. */
type CreateProjectPayload = {
  orgId: string;
  name: string;
  startDate: string; // ISO
  endDate: string | null; // ISO or null
  siteAddress: string;
  value: number;
  photoUri: string | null;
  status?: 'active' | 'on_hold' | 'completed' | 'archived';
  progress?: number;
};
type CreateProjectResponse = { projectId: string };

// ───── Inline-style helpers (kept in-file so the modal stays
//       visually consistent without adding one-off iio.css rules) ──

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
  outline: 'none',
};
const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--text-tertiary)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
};
const HELP: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--text-tertiary)',
  marginTop: 4,
};

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function parseDateKey(k: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(k)) return null;
  const [y, m, d] = k.split('-').map(Number);
  // Anchor at noon so the eventual ISO conversion doesn't slip to
  // the previous day in UTC (00:00 IST → 18:30 UTC of yesterday).
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

export function NewProjectModal({
  opened,
  onClose,
  orgId,
  onCreated,
  defaultName,
  defaultSiteAddress,
  defaultValue,
}: NewProjectModalProps) {
  const [name, setName] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [startDateKey, setStartDateKey] = useState(() => todayKey());
  const [endDateKey, setEndDateKey] = useState('');
  const [value, setValue] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setSiteAddress('');
    setStartDateKey(todayKey());
    setEndDateKey('');
    setValue('');
    setError(null);
  };

  // Re-hydrate from props every time the modal opens.
  useEffect(() => {
    if (!opened) return;
    setError(null);
    if (defaultName?.trim()) setName(defaultName.trim());
    if (defaultSiteAddress?.trim()) setSiteAddress(defaultSiteAddress.trim());
    if (typeof defaultValue === 'number' && defaultValue > 0) {
      setValue(String(defaultValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // Body scroll-lock + Esc-to-close.
  useEffect(() => {
    if (!opened) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [opened, busy, onClose]);

  if (!opened) return null;

  async function onSubmit() {
    setError(null);
    if (!orgId) {
      setError('No studio selected — refresh the page or sign in again.');
      return;
    }
    const trimmedName = name.trim();
    const trimmedAddress = siteAddress.trim();
    if (trimmedName.length < 2 || trimmedName.length > 80) {
      setError('Project name must be between 2 and 80 characters.');
      return;
    }
    if (trimmedAddress.length < 3) {
      setError(
        'Site address is required — add at least a building name or street.',
      );
      return;
    }
    const startDate = parseDateKey(startDateKey);
    if (!startDate) {
      setError('Pick a valid start date.');
      return;
    }
    const endDate = endDateKey ? parseDateKey(endDateKey) : null;
    if (endDateKey && !endDate) {
      setError('Pick a valid end date or leave it blank.');
      return;
    }
    if (endDate && endDate < startDate) {
      setError('End date must be on or after the start date.');
      return;
    }
    const numValue = parseFloat(value);
    if (!Number.isFinite(numValue) || numValue <= 0) {
      setError('Enter the project value in whole rupees (e.g. 500000).');
      return;
    }

    setBusy(true);
    try {
      const callable = httpsCallable<
        CreateProjectPayload,
        CreateProjectResponse
      >(functions, 'createProject');
      const { data } = await callable({
        orgId,
        name: trimmedName,
        startDate: startDate.toISOString(),
        endDate: endDate ? endDate.toISOString() : null,
        siteAddress: trimmedAddress,
        value: Math.round(numValue),
        photoUri: null,
        status: 'active',
        progress: 0,
      });
      reset();
      onClose();
      onCreated?.(data.projectId);
    } catch (e) {
      // Friendly message when the org hits its plan project cap —
      // server throws `failed-precondition` with details.reason +
      // .tier + .limit.
      const err = e as {
        code?: string;
        message?: string;
        details?: { reason?: string; tier?: string; limit?: number };
      };
      const isPlanLimit =
        err.code === 'functions/failed-precondition' &&
        err.details?.reason === 'plan_limit_projects';
      if (isPlanLimit) {
        setError(
          `Plan limit reached — your ${err.details?.tier ?? 'current'} plan allows up to ${err.details?.limit ?? '—'} projects. Upgrade from the Plans page to add more.`,
        );
      } else {
        setError(
          err.message ??
            "Couldn't create the project. Please try again or email hello@interioros.app.",
        );
      }
    } finally {
      setBusy(false);
    }
  }

  // Use the same slide-overlay backdrop the rest of the app's
  // slide-overs use, but render a centered card instead of a side
  // panel for this short form.
  return (
    <>
      <div
        className="slide-overlay open"
        onClick={() => !busy && onClose()}
        style={{ background: 'rgba(15,23,42,0.45)' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-project-title"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 201,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          pointerEvents: 'none',
        }}
      >
        <div
          className="card"
          style={{
            width: '100%',
            maxWidth: 540,
            background: 'var(--surface)',
            borderRadius: 12,
            boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 32px)',
            overflow: 'hidden',
            pointerEvents: 'auto',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <h2
              id="new-project-title"
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: 'var(--text-primary)',
              }}
            >
              <i
                className="ph-bold ph-folder-plus"
                style={{ marginRight: 8, color: 'var(--brand)' }}
              />
              New project
            </h2>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              aria-label="Close"
              style={{
                background: 'none',
                border: 'none',
                fontSize: 18,
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                padding: 4,
                lineHeight: 1,
              }}
            >
              <i className="ph ph-x" />
            </button>
          </div>

          {/* Body — scrolls if it overflows on short laptops */}
          <div
            style={{
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              overflowY: 'auto',
            }}
          >
            <div
              style={{
                background: 'var(--brand-bg)',
                color: 'var(--text-secondary)',
                padding: '10px 12px',
                borderRadius: 6,
                fontSize: 12,
                border: '1px solid #FECACA',
              }}
            >
              <i
                className="ph ph-info"
                style={{ marginRight: 6, color: 'var(--brand)' }}
              />
              Photo, client, and typology details can be added once the
              project is created.
            </div>

            {error ? (
              <div
                style={{
                  background: '#FEF2F2',
                  color: 'var(--critical)',
                  padding: '10px 12px',
                  borderRadius: 6,
                  fontSize: 13,
                  border: '1px solid #FECACA',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <i className="ph-bold ph-warning-circle" />
                <span style={{ flex: 1 }}>{error}</span>
              </div>
            ) : null}

            <div>
              <label style={LABEL}>Project name *</label>
              <input
                style={INPUT}
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                maxLength={80}
                placeholder="Sharma residence — duplex renovation"
                autoFocus
              />
            </div>

            <div>
              <label style={LABEL}>Site address *</label>
              <textarea
                style={{ ...INPUT, minHeight: 64, resize: 'vertical' }}
                value={siteAddress}
                onChange={(e) => setSiteAddress(e.currentTarget.value)}
                placeholder="Building name, street, area"
              />
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
              }}
            >
              <div>
                <label style={LABEL}>Start date *</label>
                <input
                  type="date"
                  style={INPUT}
                  value={startDateKey}
                  onChange={(e) =>
                    setStartDateKey(e.currentTarget.value || todayKey())
                  }
                />
              </div>
              <div>
                <label style={LABEL}>End date</label>
                <input
                  type="date"
                  style={INPUT}
                  value={endDateKey}
                  onChange={(e) => setEndDateKey(e.currentTarget.value)}
                  min={startDateKey || undefined}
                />
                <div style={HELP}>Optional — for timeline tracking.</div>
              </div>
            </div>

            <div>
              <label style={LABEL}>Project value (₹) *</label>
              <input
                type="number"
                style={{ ...INPUT, fontVariantNumeric: 'tabular-nums' }}
                value={value}
                onChange={(e) => setValue(e.currentTarget.value)}
                min={1}
                step={1}
                placeholder="500000"
              />
              <div style={HELP}>
                Total contract value in whole rupees. Used for finance
                summary + invoicing.
              </div>
            </div>
          </div>

          {/* Footer — fixed at the bottom of the modal card */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              padding: '14px 20px',
              borderTop: '1px solid var(--border)',
              background: '#FAFAFA',
            }}
          >
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={onSubmit}
              disabled={busy}
              style={{
                minWidth: 130,
                background: 'var(--brand)',
                borderColor: 'var(--brand)',
                color: '#fff',
              }}
            >
              {busy ? (
                'Creating…'
              ) : (
                <>
                  <i className="ph-bold ph-plus" /> Create project
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mantine portals (formerly inserted under <Modal>) used to
          create a sibling div that re-instantiates the open
          animation when `opened` flips. We don't need it here —
          this modal renders directly in the React tree. */}
      <div data-new-project-modal-spacer style={{ display: 'none' }} />
    </>
  );
}

// Helper-tone comment for greppers — once Mantine is fully purged
// from this surface, the only Mantine dependency NewProjectModal
// keeps is the type of the consumer-side `notifications` system.
// Consumers can keep their existing toasts wired to the
// `onCreated` callback if they want to surface a success message.
